import React from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { useUploadTrackableFile } from '../hooks/useUploadTrackableFile';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';

export interface AddTrackableFileFormProps {
  trackableListId: string;
  trackableItemId: string;
  onSuccess?: () => void;
}

interface FormData {
  description?: string;
  files: FileList | null;
}

export function AddTrackableFileForm({
  trackableListId,
  trackableItemId,
  onSuccess,
}: AddTrackableFileFormProps) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      description: '',
      files: null,
    },
  });

  const { uploadMultipleTrackableFiles, uploadProgress, isLoading } =
    useUploadTrackableFile();

  const watchedFiles = watch('files');
  const uploadingFilesRef = React.useRef<File[]>([]);

  // Show either the files being uploaded or the selected files
  const selectedFiles = React.useMemo(() => {
    if (isLoading) {
      return uploadingFilesRef.current;
    }
    return watchedFiles ? Array.from(watchedFiles) : [];
  }, [isLoading, watchedFiles]);

  const onSubmit = async (data: FormData) => {
    if (!data.files || data.files.length === 0) {
      return;
    }

    try {
      const files = Array.from(data.files);

      // Store files in ref so they persist across remounts
      uploadingFilesRef.current = files;

      await uploadMultipleTrackableFiles(files, {
        trackable_list_id: trackableListId,
        trackable_item_id: trackableItemId,
      });

      uploadingFilesRef.current = [];
      reset();

      // Invalidate queries BEFORE calling onSuccess (which closes the dialog)
      // This ensures the dialog is still mounted when queries refetch
      queryClient.invalidateQueries(['trackable-files']);

      onSuccess?.();
    } catch (error) {
      console.error('Upload failed:', error);
      uploadingFilesRef.current = [];
    }
  };

  const removeFile = (indexToRemove: number) => {
    // Don't allow removal during upload
    if (isLoading) return;

    if (!watchedFiles) return;

    const dt = new DataTransfer();
    Array.from(watchedFiles).forEach((file, index) => {
      if (index !== indexToRemove) {
        dt.items.add(file);
      }
    });

    // Update the form with new FileList
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4">
        <Field label="Description">
          <Input
            {...register('description')}
            placeholder="Enter description (optional)..."
          />
        </Field>

        <Field
          label="Images"
          required
          error={errors.files?.message}
          info="Upload tightly cropped images of the object for best results."
        >
          <div className="space-y-3">
            <Input
              {...register('files', {
                required: 'Please select at least one image.',
              })}
              type="file"
              multiple
              accept="image/*,video/*"
            />

            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {selectedFiles.length} file
                  {selectedFiles.length > 1 ? 's' : ''} selected:
                </p>
                <div className="max-h-96 space-y-1 overflow-y-auto rounded border border-neutral-200 p-2 dark:border-neutral-700">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between rounded bg-neutral-100 px-3 py-2 text-sm dark:bg-neutral-700"
                    >
                      <div className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                          {file.name}
                        </span>
                        <span className="text-neutral-500 dark:text-neutral-400">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="small"
                        onClick={() => removeFile(index)}
                        disabled={isLoading}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Field>

        {/* Upload Progress */}
        {uploadProgress.total > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Uploading: {uploadProgress.current} of {uploadProgress.total}
            </p>
            <p className="truncate text-xs text-neutral-600 dark:text-neutral-400">
              {uploadProgress.currentFile}
            </p>
            <div className="h-2 rounded-full bg-neutral-200 dark:bg-neutral-700">
              <div
                className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                style={{
                  width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <ButtonGroup>
          <Button
            type="submit"
            variant="primary"
            size="small"
            loading={isLoading}
            disabled={!selectedFiles.length}
          >
            Upload{' '}
            {selectedFiles.length > 0 &&
              `${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`}
          </Button>
        </ButtonGroup>
      </div>
    </form>
  );
}
